import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Tooltip } from "@mui/material";
import { getApiErrorMessage } from "../../../helpers/aws_api_helper";
import {
  deleteCustomerLogo,
  getCustomerLogo,
  postCustomerLogo,
} from "../../../helpers/auth_aws_helper";

const MAX_CONCURRENT = 4;
const POLL_ATTEMPTS = 6;
const POLL_DELAY_MS = 2000;

const cache = new Map();
const listeners = new Set();

let activeRequests = 0;
const requestQueue = [];

const cacheKey = (orgCode, logoId) => `${orgCode}::${logoId || ""}`;

const notify = () => {
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const runLimited = (task) =>
  new Promise((resolve, reject) => {
    const start = () => {
      activeRequests += 1;
      task()
        .then(resolve, reject)
        .finally(() => {
          activeRequests -= 1;
          const next = requestQueue.shift();
          if (next) next();
        });
    };
    if (activeRequests < MAX_CONCURRENT) start();
    else requestQueue.push(start);
  });

const httpUrl = (value) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : "";
};

const readLogoUrl = (body) =>
  httpUrl(body?.logo_url) ||
  httpUrl(body?.logoUrl) ||
  httpUrl(body?.url) ||
  httpUrl(body?.view_url) ||
  httpUrl(body?.viewUrl);

const readScanStatus = (body) => {
  const value = body?.scan_status ?? body?.scanStatus ?? body?.status;
  return typeof value === "string" ? value.trim() : "";
};

const readLogoId = (body) => {
  const value = body?.logo_id ?? body?.logoId;
  return value == null ? "" : String(value).trim();
};

const isFailedScan = (scanStatus) =>
  /fail|error|infect|malware|reject/i.test(scanStatus || "");

const responseBody = (response) => {
  const body = response?.data && typeof response.data === "object" ? response.data : {};
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    return { ...body, ...body.data };
  }
  return body;
};

export const readCustomerLogoId = (customer) => {
  const value = customer?.logo_id ?? customer?.logoId;
  return value == null ? "" : String(value).trim();
};

const storeLogo = (orgCode, logoId, result) => {
  const entry = {
    phase: result.url ? "ready" : result.error ? "error" : "empty",
    url: result.url || "",
    scanStatus: result.scanStatus || "",
    logoId: result.logoId || logoId || "",
    error: Boolean(result.error),
    message: result.message || "",
  };
  cache.set(cacheKey(orgCode, entry.logoId), entry);
  cache.set(cacheKey(orgCode, ""), entry);
  if (logoId && logoId !== entry.logoId) {
    cache.set(cacheKey(orgCode, logoId), entry);
  }
  notify();
  return entry;
};

const loadCustomerLogo = (orgCode, logoId, { force = false } = {}) => {
  const key = cacheKey(orgCode, logoId);
  const existing = cache.get(key);
  if (!force) {
    if (existing?.promise) return existing.promise;
    if (existing && existing.phase !== "loading") return Promise.resolve(existing);
  }

  const promise = runLimited(async () => {
    const response = await getCustomerLogo(orgCode, logoId);
    const httpStatus = response?.status;
    if (httpStatus === 404) {
      return storeLogo(orgCode, logoId, { url: "", scanStatus: "", logoId });
    }
    if (httpStatus !== 200) {
      return storeLogo(orgCode, logoId, {
        url: "",
        error: true,
        message: getApiErrorMessage(responseBody(response)) || "Could not load logo.",
        logoId,
      });
    }
    const body = responseBody(response);
    return storeLogo(orgCode, logoId, {
      url: readLogoUrl(body),
      scanStatus: readScanStatus(body),
      logoId: readLogoId(body) || logoId,
    });
  }).catch(() =>
    storeLogo(orgCode, logoId, {
      url: "",
      error: true,
      message: "Could not load logo.",
      logoId,
    })
  );

  cache.set(key, { phase: "loading", promise });
  return promise;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function useCustomerLogo(orgCode, logoId, enabled) {
  const [logo, setLogo] = useState({
    phase: "idle",
    url: "",
    scanStatus: "",
    logoId: "",
    error: false,
    message: "",
  });

  useEffect(() => {
    if (!enabled || !orgCode) {
      setLogo({
        phase: "idle",
        url: "",
        scanStatus: "",
        logoId: "",
        error: false,
        message: "",
      });
      return undefined;
    }

    let cancelled = false;
    const apply = () => {
      const cached = cache.get(cacheKey(orgCode, logoId));
      if (cached && cached.phase !== "loading") {
        if (!cancelled) setLogo(cached);
        return;
      }
      loadCustomerLogo(orgCode, logoId).then((result) => {
        if (!cancelled && result) setLogo(result);
      });
    };

    apply();
    const unsubscribe = subscribe(apply);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [enabled, orgCode, logoId]);

  return logo;
}

const getLogoContentType = (file) => {
  const type = String(file?.type || "").toLowerCase();
  if (type === "image/png") return "image/png";
  if (type === "image/jpeg" || type === "image/jpg" || type === "image/pjpeg") {
    return "image/jpeg";
  }
  const name = String(file?.name || "").toLowerCase();
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  return "";
};

export async function uploadCustomerLogo(orgCode, file) {
  const contentType = getLogoContentType(file);
  if (!contentType) {
    return { ok: false, message: "Only PNG or JPEG images are allowed." };
  }

  const response = await postCustomerLogo(orgCode, file.name, contentType);
  const httpStatus = response?.status;
  const data = responseBody(response);
  const uploadUrl = data.upload_url || data.uploadUrl;
  if ((httpStatus !== 200 && httpStatus !== 201) || !uploadUrl) {
    return {
      ok: false,
      message: getApiErrorMessage(data) || "Could not get upload URL",
    };
  }

  const uploadContentType = data.content_type || data.contentType || contentType;
  const putResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": uploadContentType },
  });
  if (!putResponse.ok) {
    return {
      ok: false,
      message:
        putResponse.status === 403
          ? "Upload denied (403). File type may not match; try again or contact support."
          : "Upload failed",
    };
  }

  const uploadedLogoId = readLogoId(data);
  let latest = null;
  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
    if (attempt > 0) await wait(POLL_DELAY_MS);
    latest = await loadCustomerLogo(orgCode, uploadedLogoId, { force: true });
    if (latest?.url || isFailedScan(latest?.scanStatus)) break;
  }

  if (isFailedScan(latest?.scanStatus)) {
    return {
      ok: false,
      message: latest.scanStatus
        ? `Logo scan did not succeed (${latest.scanStatus}).`
        : "Logo scan did not succeed.",
      logo: latest,
    };
  }

  return { ok: true, logo: latest };
}

export async function removeCustomerLogo(orgCode) {
  const response = await deleteCustomerLogo(orgCode);
  const httpStatus = response?.status;
  if (httpStatus !== 200 && httpStatus !== 204) {
    return {
      ok: false,
      message: getApiErrorMessage(responseBody(response)) || "Failed to remove logo.",
    };
  }
  storeLogo(orgCode, "", { url: "", scanStatus: "", logoId: "" });
  return { ok: true };
}

const CustomerLogoMark = ({ orgCode, logoId }) => {
  const logo = useCustomerLogo(orgCode, logoId, Boolean(orgCode));
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [logo.url]);

  if (!logo.url || broken) return null;

  return (
    <Tooltip title="Sub-organization" arrow>
      <Box
        component="img"
        src={logo.url}
        alt=""
        aria-label="Sub-organization logo"
        onError={() => setBroken(true)}
        sx={{
          width: 24,
          height: 24,
          objectFit: "contain",
          borderRadius: "4px",
          flexShrink: 0,
          border: "1px solid #e2e8f0",
          backgroundColor: "#fff",
          display: "block",
        }}
      />
    </Tooltip>
  );
};

CustomerLogoMark.propTypes = {
  orgCode: PropTypes.string,
  logoId: PropTypes.string,
};

export default CustomerLogoMark;
